// Location landing pages — unique differentiating content per city.
// Each entry has enough distinct information (drive time, transit, popular
// services for that area, cultural/demographic notes, nearby landmarks) that
// Google treats these as unique pages, not doorway pages.

export const LOCATIONS = [
  {
    slug: "los-angeles",
    tier: 1,
    name: "Los Angeles",
    heroLabel: "Serving Greater Los Angeles",
    metaTitle: "Medical Spa Los Angeles",
    metaDescription:
      "Physician-led medical spa serving Los Angeles from our Alhambra clinic. FDA-cleared aesthetics, GLP-1, hormone and longevity medicine. 15-min drive from DTLA.",
    driveInfo: {
      distance: "9 miles east of Downtown LA",
      time: "15–25 minutes via I-10 East or the 60",
      exits: "Fremont Ave / Garfield Ave exit off I-10",
    },
    intro:
      "Los Angeles patients drive to our Alhambra clinic for one reason: a physician-led boutique alternative to the celebrity injector mills of Beverly Hills and West Hollywood — with real medical oversight, evidence-based protocols, and none of the manufactured urgency.",
    demographic:
      "Our LA patients skew professional and health-forward. They've usually tried the West-side injector circuit and want a clinical setting where a physician actually sees them each visit. Many split time between aesthetics and precision-health work (biomarkers, hormones, GLP-1).",
    popularServices: [
      "injectables-fillers",
      "xerf-structural-tightening",
      "precision-weight-management",
      "hormone-optimization",
      "longevity-medicine",
    ],
    nearbyLandmarks: [
      "Downtown LA (15 min via I-10)",
      "Little Tokyo & Arts District (18 min)",
      "USC / Exposition Park (22 min)",
      "Los Feliz & Silver Lake (20 min via I-5)",
      "Griffith Observatory (25 min)",
    ],
    culturalNote:
      "Our team speaks English, Korean, Mandarin, and Tagalog — reflecting the LA metropolitan patient population we serve.",
  },
  {
    slug: "east-los-angeles",
    tier: 1,
    name: "East Los Angeles",
    heroLabel: "Serving East LA",
    metaTitle: "Medical Spa East LA",
    metaDescription:
      "Physician-led aesthetic and longevity clinic serving East LA — 4 miles west via I-10. Melanin-safe lasers, injectables, GLP-1, hormone care.",
    driveInfo: {
      distance: "4 miles east of East LA (90022)",
      time: "10 minutes via Cesar Chavez Ave or I-10 East",
      exits: "Fremont Ave exit from the 10",
    },
    intro:
      "For patients in East LA — 90022, 90023, Boyle Heights, City Terrace — Akira is the closest physician-led medical spa with truly melanin-safe laser and IPL protocols. That matters: many mid-tier medspas run one-setting-fits-all lasers that leave hyperpigmentation on Fitzpatrick IV–VI skin. We calibrate every device for skin type.",
    demographic:
      "The East LA community is majority Latino, and skin tone diversity is the norm. Our physicians and injectors are specifically trained on Fitzpatrick III–VI protocols for melasma, post-inflammatory hyperpigmentation, and safe laser resurfacing on richly pigmented skin.",
    popularServices: [
      "ipl-photofacial",
      "thulium-laser",
      "injectables-fillers",
      "skin-microneedling",
      "prp-prf-regenerative-therapy",
    ],
    nearbyLandmarks: [
      "East LA Civic Center (8 min)",
      "Belvedere Park (10 min)",
      "Cal State LA (12 min)",
      "Boyle Heights (12 min)",
      "Whittier Blvd corridor (10 min)",
    ],
    culturalNote:
      "We provide Spanish-language consultations by request — please note when you book. Our clinic is a short drive from the Metro Gold Line Atlantic Station via East LA-Atlantic corridor.",
  },
  {
    slug: "pasadena",
    tier: 1,
    name: "Pasadena",
    heroLabel: "Serving Pasadena",
    metaTitle: "Medical Spa Pasadena",
    metaDescription:
      "Pasadena's premier physician-led alternative for longevity medicine, hormone optimization, and evidence-based aesthetics. 12 minutes south via I-210 / I-710.",
    driveInfo: {
      distance: "6 miles south of Old Pasadena",
      time: "12–18 minutes via I-210 West or Fair Oaks Ave",
      exits: "Fremont Ave exit off I-210 / I-710",
    },
    intro:
      "Pasadena patients — particularly those familiar with the standard-of-care at Huntington Health and the concierge practices along Colorado Blvd — choose Akira for what those settings can't offer: unhurried physician time, an integrated aesthetics-plus-longevity model, and access to modern precision-health testing (multi-omics, biological age, comprehensive biomarkers) under one roof.",
    demographic:
      "Our Pasadena patient base is professional, well-informed, and often already engaged with longevity or preventive-medicine thinking. They come for the science-first framing: what does the data actually show, what should we measure, what will actually move the needle.",
    popularServices: [
      "longevity-medicine",
      "hormone-optimization",
      "genetics-microbiome-biomarkers",
      "precision-weight-management",
      "xerf-structural-tightening",
      "functional-medicine",
    ],
    nearbyLandmarks: [
      "Old Pasadena & Colorado Blvd (12 min)",
      "Huntington Library (10 min)",
      "Caltech (15 min)",
      "Pasadena City College (13 min)",
      "Rose Bowl area (18 min)",
    ],
    culturalNote:
      "Our Precision Health consultant Erika Tajra, MBBS, MSc — trained at UCL (London) and Oxford — brings the international-longevity-clinic caliber of care many Pasadena patients previously sought abroad.",
  },
  {
    slug: "monterey-park",
    tier: 2,
    name: "Monterey Park",
    heroLabel: "Serving Monterey Park",
    metaTitle: "Medical Spa Monterey Park",
    metaDescription:
      "Physician-led aesthetics for Monterey Park's Chinese-American community. Mandarin + Korean spoken. IPL, Thulium laser, melasma-safe protocols. 2 miles south.",
    driveInfo: {
      distance: "2 miles north of Monterey Park",
      time: "6–10 minutes via Garfield Ave or Atlantic Blvd",
      exits: "Straight up Garfield Ave from Monterey Park",
    },
    intro:
      "For Monterey Park's Chinese-American and Taiwanese community, Akira is the neighborhood physician-led option — with staff who speak Mandarin and Korean, protocols calibrated for East-Asian skin phenotypes, and honest conversations about which treatments are appropriate (and which aren't) for melasma-prone skin.",
    demographic:
      "Monterey Park has one of the largest Chinese-American populations in the U.S. Common patient concerns include melasma, PIH after acne or over-aggressive treatments elsewhere, undereye discoloration, and jawline contouring. We're conservative with lasers, aggressive with sun-protection education, and thoughtful about filler placement for East-Asian facial anatomy.",
    popularServices: [
      "ipl-photofacial",
      "thulium-laser",
      "injectables-fillers",
      "skin-microneedling",
      "advanced-facial-red-light-therapy",
      "visia-skin-analysis",
    ],
    nearbyLandmarks: [
      "Atlantic Times Square (5 min)",
      "Garvey Ranch Park (8 min)",
      "Monterey Park Historical Museum (7 min)",
      "New Avenue / Garvey Ave restaurants (5 min)",
      "East LA College (10 min)",
    ],
    culturalNote:
      "Kelly Chang, RN — one of our aesthetic nurses — speaks fluent Mandarin, Korean, and English. Bilingual consultations are available; just note your preference when you book.",
  },
  {
    slug: "san-gabriel",
    tier: 2,
    name: "San Gabriel",
    heroLabel: "Serving San Gabriel",
    metaTitle: "Medical Spa San Gabriel",
    metaDescription:
      "San Gabriel patients: 5 min north to Alhambra. Physician-led aesthetics with Mandarin-speaking staff and melasma-safe protocols.",
    driveInfo: {
      distance: "1 mile north of downtown San Gabriel",
      time: "5–8 minutes via Main St, Valley Blvd, or Del Mar Ave",
      exits: "Straight up Del Mar or Garfield from Valley Blvd",
    },
    intro:
      "San Gabriel is our closest neighboring city — most of our patients from here walk in for consults or drop by on the way home from Valley Blvd. If you live in the 91776 or 91775, we're the local physician-led option that doesn't require a Pasadena or Downtown LA commute for aesthetic + longevity care.",
    demographic:
      "San Gabriel has one of the largest Chinese and Vietnamese-American populations in the SGV. Common patient concerns mirror what we see from Monterey Park and Arcadia: melasma, undereye pigmentation, jawline contouring appropriate for East-Asian facial anatomy, and post-treatment hyperpigmentation from over-aggressive laser work elsewhere. We calibrate conservatively for Fitzpatrick III–V skin.",
    popularServices: [
      "ipl-photofacial",
      "thulium-laser",
      "injectables-fillers",
      "visia-skin-analysis",
      "skin-microneedling",
      "advanced-facial-red-light-therapy",
    ],
    nearbyLandmarks: [
      "San Gabriel Square / Focus Plaza (5 min)",
      "Valley Blvd restaurants (4 min)",
      "San Gabriel Mission (7 min)",
      "Vincent Lugo Park (6 min)",
      "Mission District shops (5 min)",
    ],
    culturalNote:
      "Kelly Chang, RN speaks fluent Mandarin, Korean, and English — many of our San Gabriel patients book bilingual consultations with her. We're a 5-minute drive up Del Mar or Garfield from most San Gabriel neighborhoods.",
  },
  {
    slug: "rosemead",
    tier: 2,
    name: "Rosemead",
    heroLabel: "Serving Rosemead",
    metaTitle: "Medical Spa Rosemead",
    metaDescription:
      "Rosemead patients: 5 minutes to Alhambra for physician-led injectables, IPL, GLP-1, and hormone care. Mandarin + Vietnamese-friendly staff.",
    driveInfo: {
      distance: "3 miles east of Rosemead",
      time: "5–8 minutes via Valley Blvd, Garvey Ave, or Rosemead Blvd",
      exits: "Straight down Valley Blvd from Rosemead into Alhambra",
    },
    intro:
      "For Rosemead residents, Akira is the neighborhood alternative to driving to Pasadena or Beverly Hills for real medical care. We're 5 minutes east on Valley Blvd — closer than the drive-thru at the Panda Express headquarters — with physician oversight on every treatment and staff who speak Mandarin and Korean.",
    demographic:
      "Rosemead has one of the largest Vietnamese-American and Chinese-American populations in Los Angeles County. Our Rosemead patients typically come in for melasma care, IPL for sun-related pigmentation, injectables calibrated for East-Asian anatomy, and GLP-1 weight management. We're honest about which treatments give real results for your skin type versus which are marketing.",
    popularServices: [
      "ipl-photofacial",
      "thulium-laser",
      "injectables-fillers",
      "precision-weight-management",
      "visia-skin-analysis",
      "skin-microneedling",
    ],
    nearbyLandmarks: [
      "Rosemead Place shopping center (5 min)",
      "Garvey Ave restaurants (4 min)",
      "Rosemead High School (7 min)",
      "Panda Express HQ (5 min)",
      "El Monte / San Gabriel Blvd corridor (8 min)",
    ],
    culturalNote:
      "Kelly Chang, RN speaks Mandarin, Korean, and English — bilingual consultations available for Rosemead patients who prefer to discuss care in their first language.",
  },
  {
    slug: "temple-city",
    tier: 2,
    name: "Temple City",
    heroLabel: "Serving Temple City",
    metaTitle: "Medical Spa Temple City",
    metaDescription:
      "Temple City patients: 10 min to Alhambra. Physician-led injectables, lasers, hormone care, GLP-1. Mandarin-speaking staff.",
    driveInfo: {
      distance: "5 miles east of Temple City",
      time: "10–14 minutes via Las Tunas Dr or Rosemead Blvd",
      exits: "Rosemead Blvd south, then Main St west into Alhambra",
    },
    intro:
      "Temple City's Chinese-American majority patient base wants what all our SGV patients want: careful, culturally-informed care from a physician-led clinic that isn't going to over-treat pigmentation-prone skin. Ten minutes west on Las Tunas Dr puts you in our lobby.",
    demographic:
      "Temple City is a quiet, family-oriented suburb with a large Chinese-American population — many of our patients here are parents in their 40s–60s starting on preventive-health work (hormones, biomarkers, GLP-1) or exploring subtle aesthetic maintenance for the first time. Trust and reversibility matter more than trends. We treat conservatively and explain everything.",
    popularServices: [
      "hormone-optimization",
      "precision-weight-management",
      "injectables-fillers",
      "ipl-photofacial",
      "longevity-medicine",
      "genetics-microbiome-biomarkers",
    ],
    nearbyLandmarks: [
      "Live Oak Park (10 min)",
      "Temple City Camellia Festival grounds (11 min)",
      "Las Tunas Dr shops (10 min)",
      "Temple City High School (12 min)",
      "Arcadia border (5 min)",
    ],
    culturalNote:
      "For Temple City patients considering hormone or longevity workups, we can bundle initial labs with your first visit so you don't need to make the drive twice for baseline testing.",
  },
  {
    slug: "san-marino",
    tier: 2,
    name: "San Marino",
    heroLabel: "Serving San Marino",
    metaTitle: "Medical Spa San Marino",
    metaDescription:
      "Boutique physician-led clinic for San Marino residents. Longevity, hormone optimization, XERF non-surgical lifting. 8 minutes via Huntington Dr.",
    driveInfo: {
      distance: "3 miles east of San Marino",
      time: "8–12 minutes via Huntington Dr or Atlantic Blvd",
      exits: "Straight down Atlantic to Main St, then Garfield",
    },
    intro:
      "San Marino patients expect concierge-level medicine — and typically already have a preferred internist, dermatologist, and OB/GYN. What they're often missing is a single clinic that integrates aesthetics with proactive longevity work without the marketing gloss of the Beverly Hills anti-aging shops. That's Akira.",
    demographic:
      "San Marino is one of California's most affluent zip codes. Our patients here tend to be in their 40s–60s, already engaged with health optimization, and looking for a boutique physician-led setting where their care isn't outsourced to a rotating cast of RNs. Consistency and physician time are the value proposition.",
    popularServices: [
      "hormone-optimization",
      "longevity-medicine",
      "genetics-microbiome-biomarkers",
      "xerf-structural-tightening",
      "injectables-fillers",
      "prp-prf-regenerative-therapy",
    ],
    nearbyLandmarks: [
      "Huntington Library (5 min)",
      "Lacy Park (8 min)",
      "Mission St shops (7 min)",
      "San Marino High School (8 min)",
      "Cal-Tech / Pasadena border (10 min)",
    ],
    culturalNote:
      "For patients whose spouses or partners also want to be seen, we offer joint longevity workups — many couples come in together for baseline biomarker + hormone panels.",
  },
  {
    slug: "south-pasadena",
    tier: 2,
    name: "South Pasadena",
    heroLabel: "Serving South Pasadena",
    metaTitle: "Medical Spa South Pasadena",
    metaDescription:
      "Physician-led aesthetic and wellness care for South Pasadena residents. 10 minutes via Fair Oaks or I-110. GLP-1, injectables, precision health.",
    driveInfo: {
      distance: "5 miles southwest of South Pasadena",
      time: "10–15 minutes via Fair Oaks Ave or Fremont Ave",
      exits: "Fremont Ave through Alhambra to Garfield",
    },
    intro:
      "South Pasadena has a tight-knit, health-conscious professional community — the kind who read the ingredients on their skincare and ask about long-term data before starting a new protocol. Akira's evidence-first approach lands well here: we explain the science, quote the studies, and help you decide.",
    demographic:
      "Our South Pasadena patient base skews toward dual-career families, often with young children, seeking wellness care that fits into a full schedule. GLP-1 and metabolic health, hormone care (perimenopause is a common intake reason), and low-downtime aesthetic maintenance are the most-requested categories.",
    popularServices: [
      "precision-weight-management",
      "hormone-optimization",
      "injectables-fillers",
      "advanced-facial-red-light-therapy",
      "iv-therapy",
      "skin-microneedling",
    ],
    nearbyLandmarks: [
      "Fair Oaks Ave corridor (10 min)",
      "South Pasadena Farmers Market (12 min)",
      "Arroyo Seco (14 min)",
      "Mission West shops (11 min)",
      "Garfield Park (13 min)",
    ],
    culturalNote:
      "We book same-week appointments for most services, and virtual follow-ups are available for hormone / GLP-1 / longevity care so you don't need to make the drive twice.",
  },
  {
    slug: "arcadia",
    tier: 2,
    name: "Arcadia",
    heroLabel: "Serving Arcadia",
    metaTitle: "Medical Spa Arcadia",
    metaDescription:
      "Arcadia patients: 15 minutes to Alhambra for physician-led aesthetics, longevity, and precision health. Mandarin-speaking staff. Melasma-safe lasers.",
    driveInfo: {
      distance: "8 miles east of Arcadia",
      time: "15–20 minutes via I-210 West or Huntington Dr",
      exits: "Fremont Ave exit off I-210",
    },
    intro:
      "Arcadia's largely Chinese-American professional and medical community has a high bar for clinical care. Akira fits that expectation: MD-directed, evidence-based, careful with pigmentary risks common in East-Asian skin, and unwilling to over-treat when watchful conservation is the right answer.",
    demographic:
      "Many Arcadia patients come to us via referral from their internists at Methodist Hospital of Southern California or from family who already see us. Common intake reasons: melasma flare-ups, jaw-line contouring, longevity workups, and hormone care for peri- and post-menopausal women.",
    popularServices: [
      "ipl-photofacial",
      "thulium-laser",
      "visia-skin-analysis",
      "hormone-optimization",
      "longevity-medicine",
      "injectables-fillers",
    ],
    nearbyLandmarks: [
      "Westfield Santa Anita (15 min)",
      "Santa Anita Park (16 min)",
      "Methodist Hospital of Southern California (13 min)",
      "The Arboretum (17 min)",
      "Downtown Arcadia (14 min)",
    ],
    culturalNote:
      "Mandarin-language consultations are available with our RN Kelly Chang. Referrals from patients in Arcadia make up one of our largest word-of-mouth patient pipelines.",
  },
  {
    slug: "silver-lake",
    tier: 3,
    name: "Silver Lake",
    heroLabel: "Serving Silver Lake",
    metaTitle: "Medical Spa near Silver Lake",
    metaDescription:
      "Silver Lake residents: 20 minutes east via I-5 / I-10. Physician-led injectables, PRP, RF microneedling. Boutique alternative to Silver Lake / Los Feliz spas.",
    driveInfo: {
      distance: "10 miles east of Silver Lake",
      time: "20–25 minutes via I-5 South or I-10 East",
      exits: "I-5 South to I-10 East, exit Fremont Ave",
    },
    intro:
      "Silver Lake's creative professionals bring an unusually informed baseline to their consultations — they've usually already researched biostimulators, tested retinoids properly, and know the difference between HA fillers and Sculptra. That makes for great conversations. We meet you at your level.",
    demographic:
      "Silver Lake and Los Feliz patients tend to want subtle, natural-looking results — no overfilled cheeks, no frozen foreheads. Injectables are the most-requested service, followed by PRP and RF microneedling for texture and tone. Longevity workups are a growing category.",
    popularServices: [
      "injectables-fillers",
      "prp-prf-regenerative-therapy",
      "rf-microneedling",
      "skin-microneedling",
      "advanced-facial-red-light-therapy",
      "genetics-microbiome-biomarkers",
    ],
    nearbyLandmarks: [
      "Sunset Junction (20 min)",
      "Silver Lake Reservoir (22 min)",
      "Los Feliz Village (18 min)",
      "Griffith Park (23 min)",
      "Echo Park Lake (22 min)",
    ],
    culturalNote:
      "Evening appointments are available Tuesday–Thursday for the professional-hours crowd — please request when booking.",
  },
  {
    slug: "highland-park",
    tier: 3,
    name: "Highland Park",
    heroLabel: "Serving Highland Park",
    metaTitle: "Medical Spa Highland Park",
    metaDescription:
      "Highland Park patients: 15 minutes to Alhambra for physician-led aesthetics. Injectables, lasers, GLP-1, hormone optimization. Melasma-safe protocols.",
    driveInfo: {
      distance: "5 miles southeast of Highland Park",
      time: "15 minutes via I-110 South or Fremont Ave",
      exits: "Fremont Ave exit off I-110 / I-10",
    },
    intro:
      "Highland Park's demographic mix — long-time Latino families and newer creative-professional transplants — brings a broad range of aesthetic and wellness needs to our clinic. We're the closest physician-led option in the immediate area, with staff trained across skin tones and comfortable with both preventive and corrective work.",
    demographic:
      "We see a lot of first-visit patients from Highland Park who've been burned by cheap-Groupon medspas elsewhere and want an actual physician overseeing their care. GLP-1 weight management, injectables, and IPL for pigmentation are our top three requests from the 90042.",
    popularServices: [
      "injectables-fillers",
      "ipl-photofacial",
      "precision-weight-management",
      "skin-microneedling",
      "prp-hair-restoration",
      "iv-therapy",
    ],
    nearbyLandmarks: [
      "York Blvd corridor (15 min)",
      "Figueroa St corridor (14 min)",
      "Highland Park Bowl (15 min)",
      "Occidental College (18 min)",
      "Debs Park (13 min)",
    ],
    culturalNote:
      "Spanish-language consultations available by request. We're closer than most Highland Park patients realize — right off I-110.",
  },
  {
    slug: "downtown-la",
    tier: 3,
    name: "Downtown LA",
    heroLabel: "Serving Downtown Los Angeles",
    metaTitle: "Medical Spa near Downtown LA",
    metaDescription:
      "DTLA professionals: 15 minutes east via I-10 for physician-led aesthetics, IV therapy, injectables, and precision health. Same-day appointments available.",
    driveInfo: {
      distance: "9 miles east of Downtown LA",
      time: "15–20 minutes via I-10 East",
      exits: "Fremont Ave exit off I-10",
    },
    intro:
      "For Downtown LA professionals, Akira sits in the sweet spot: close enough for a lunchtime IV or an after-work injectable touch-up, boutique enough that you're not sitting in the waiting-room churn of a west-side celebrity practice, and physician-led enough that your care isn't a checklist run by a rotating team.",
    demographic:
      "Our DTLA patient base skews professional — legal, finance, healthcare adjacent, tech — and they optimize for time. IV therapy for cognitive performance / hangover recovery, quick injectable maintenance, and GLP-1 initiations are the most common intake reasons. Many stack their appointments with lab draws for hormone or longevity panels.",
    popularServices: [
      "iv-therapy",
      "injectables-fillers",
      "precision-weight-management",
      "hormone-optimization",
      "advanced-facial-red-light-therapy",
      "body-composition-analysis",
    ],
    nearbyLandmarks: [
      "Arts District (18 min)",
      "Little Tokyo (17 min)",
      "USC / Exposition Park (22 min)",
      "Union Station (16 min)",
      "Bunker Hill (20 min)",
    ],
    culturalNote:
      "Lunchtime appointments (12–2pm Tues–Fri) are held for downtown professionals. Booking a same-day IV drip is usually possible if you call before 11am.",
  },
  {
    slug: "eagle-rock",
    tier: 3,
    name: "Eagle Rock",
    heroLabel: "Serving Eagle Rock",
    metaTitle: "Medical Spa Eagle Rock",
    metaDescription:
      "Eagle Rock patients: 18 minutes to Alhambra. Physician-led injectables, IPL, GLP-1, hormone optimization. Melasma-safe lasers. Book online.",
    driveInfo: {
      distance: "7 miles east of Eagle Rock",
      time: "18 minutes via Colorado Blvd or I-134 East",
      exits: "Fremont Ave off I-210 / I-134",
    },
    intro:
      "Eagle Rock's family-and-creative-professional demographic wants the same thing most Akira patients want: real medicine, real oversight, and a clinic that treats them like an individual rather than a chart number. We're on the east side of the 210 — closer than the West-side alternatives most Eagle Rock residents drive to.",
    demographic:
      "Eagle Rock brings us a mix — parents in their 30s–40s starting on GLP-1s and perimenopause care, plus creative professionals who care about long-term skin health more than trendy fillers. We do a lot of maintenance-tier aesthetic care here and a lot of preventive longevity work.",
    popularServices: [
      "injectables-fillers",
      "precision-weight-management",
      "hormone-optimization",
      "skin-microneedling",
      "advanced-facial-red-light-therapy",
      "functional-medicine",
    ],
    nearbyLandmarks: [
      "Colorado Blvd corridor (18 min)",
      "Occidental College (16 min)",
      "Eagle Rock Plaza (18 min)",
      "Highland Park border (10 min)",
      "Glendale (22 min)",
    ],
    culturalNote:
      "For Eagle Rock patients, the drive is easier than it looks on a map — the 210 and Colorado Blvd both cut straight through to Alhambra without downtown traffic.",
  },
];

export const getLocationBySlug = (slug) => LOCATIONS.find((l) => l.slug === slug) || null;

// Cities used in the site-wide `areaServed` schema
export const SERVICE_AREA_CITIES = LOCATIONS.map((l) => l.name);
